import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AdId,
  useDebugger,
  useNavigation,
  usePageLoader,
  usePageView,
  useSiteMeta,
} from '../../../contexts'
import { utils } from '../../Player/Helpers'
import {
  Container,
  Content,
  PortalLogo,
  SkipButton,
  Strip,
  VodLogo,
} from './Maavaron.styles'
import { he } from '../../../locales/he'
import { useRootStore } from '../../../store'
import { ottLog, taboolaLog } from '../../../utils'
import { isDesktop, isNativeApp } from '../../../client'
import { useFL } from '../../../hooks/useFL'
import { Ad } from '../../../sharedComponents'

export const activeRos = 'Maavaron_ros'
export const Maavaron = ({
  theme = 'portal',
}: {
  theme?: 'portal' | 'vod' | 'ott'
}) => {
  const { direction: navDirection, directionRef: navDirectionRef } =
    useNavigation()
  const { sessionPageView } = usePageView()
  const { dailyClicksCount, setIsShowingMaavaron } = useRootStore()
  const router = useRouter()
  const { setLoading } = usePageLoader()
  const SiteMeta = useSiteMeta()
  const { setLog } = useDebugger()
  // const pageViewedInSession = useRef(0)
  const { fontColor, isDarkTheme, isFaceLift } = useFL()
  const [showMaavaron, setShowMaavaron] = useState(false)
  const [outOfPage, setOutOfPage] = useState(true)
  const [MaavaronId, setMaavaronId] = useState<
    false | `Maavaron_${'Desktop' | 'ros'}${'' | '_next'}`
  >(false)
  const receiveTimeOut = useRef<NodeJS.Timeout | null>(null)
  const maavronTimeout = useRef<null | NodeJS.Timeout>(null)

  const [slotRenderEventReceived, setSlotRenderEventReceived] = useState<{
    received: boolean
    isEmpty: null | boolean
  }>({
    received: false,
    isEmpty: null,
  })

  useEffect(() => {
    receiveTimeOut.current && clearTimeout(receiveTimeOut.current)
    if (slotRenderEventReceived.received) {
      if (slotRenderEventReceived.isEmpty) {
        setLoading(false)
      } else {
        receiveTimeOut.current = setTimeout(() => {
          setLoading(false)
        }, 1500)
      }
    }
  }, [setLoading, slotRenderEventReceived])

  const maavronConfig = useMemo(
    () =>
      SiteMeta?.config?.maavaron || {
        desktop: 5,
        mobile: 10,
      },
    [SiteMeta?.config?.maavaron]
  )

  const clearInterstitialTimeOut = () => {
    if (maavronTimeout.current) {
      clearTimeout(maavronTimeout.current)
    }
  }
  const closeMaavaron = useCallback(() => {
    // taboolaLog({ toBeLogged: { msg: 'show taboola timeout (push)' } })
    setOutOfPage(true)
    setIsShowingMaavaron(false)
  }, [setIsShowingMaavaron])

  // const getDailyClicksCount = (): number => {
  //     const clicksCount = sessionStorage.getItem('ClicksCount')
  //     return Number(clicksCount)
  // }

  useEffect(() => {
    const routeChangeComplete = () => {
      if (MaavaronId && showMaavaron) {
        const to =
          (utils.isMobile() ? maavronConfig.mobile : maavronConfig.desktop) +
          0.5
        setLog(prev => [...prev, `time out for interstitial is ${to}s`])
        maavronTimeout.current = setTimeout(() => {
          setLog(prev => [...prev, 'interstitial time out close'])
          closeMaavaron()
        }, to * 1000)
      }
    }
    router.events.on('routeChangeComplete', routeChangeComplete)
    // if ((MaavaronId && showMaavaron) || isNativeApp()) {
    //   const to =
    //     (utils.isMobile() ? maavronConfig.mobile : maavronConfig.desktop) + 0.5
    //   setLog(prev => [...prev, `time out for interstitial is ${to}s`])
    //   maavronTimeout.current = setTimeout(() => {
    //     setLog(prev => [...prev, 'interstitial time out close'])
    //     closeMaavaron()
    //   }, to * 1000)
    // }
    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete)
      if (maavronTimeout.current) {
        return clearTimeout(maavronTimeout.current)
      }
    }
  }, [
    maavronTimeout,
    setIsShowingMaavaron,
    MaavaronId,
    showMaavaron,
    closeMaavaron,
    maavronConfig.mobile,
    maavronConfig.desktop,
    setLog,
    router.events,
  ])

  useEffect(() => {
    // Router-event-based timeouts above (routeChangeStart/routeChangeComplete)
    // never fire on a hard/direct navigation - there's no client-side route
    // change on the page that mounted this component. Without a fallback tied
    // to the component's own state, a blocked ad request (e.g. by an ad
    // blocker) that never reports back leaves isShowingMaavaron stuck `true`
    // forever, which blocks the page's video player from ever loading.
    if (!MaavaronId || !showMaavaron) {
      return
    }
    const to =
      (utils.isMobile() ? maavronConfig.mobile : maavronConfig.desktop) + 0.5
    const timer = setTimeout(closeMaavaron, to * 1000)
    return () => clearTimeout(timer)
  }, [
    MaavaronId,
    showMaavaron,
    maavronConfig.mobile,
    maavronConfig.desktop,
    closeMaavaron,
  ])

  useEffect(() => {
    if (slotRenderEventReceived.received && !slotRenderEventReceived.isEmpty) {
      taboolaLog({ toBeLogged: { msg: 'reset time out' } })
      clearInterstitialTimeOut()
      maavronTimeout.current = setTimeout(
        closeMaavaron,
        ((utils.isMobile() ? maavronConfig.mobile : maavronConfig.desktop) +
          0.5) *
          1000
      )
    }
  }, [
    closeMaavaron,
    maavronConfig.desktop,
    maavronConfig.mobile,
    slotRenderEventReceived.isEmpty,
    slotRenderEventReceived.received,
  ])

  useEffect(() => {
    if (isDesktop()) {
      setMaavaronId('Maavaron_Desktop')
    } else if (!isNativeApp()) {
      setMaavaronId(activeRos)
    }
  }, [])

  useEffect(() => {
    setIsShowingMaavaron(true)
    const onRouteChangeStart = () => {
      maavronTimeout.current = setTimeout(
        closeMaavaron,
        ((utils.isMobile() ? maavronConfig.mobile : maavronConfig.desktop) +
          0.5) *
          1000
      )
      if (navDirectionRef.current !== 'back') {
        setIsShowingMaavaron(true)
      }
      setSlotRenderEventReceived({
        isEmpty: null,
        received: false,
      })
    }
    router.events.on('routeChangeStart', onRouteChangeStart)
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [
    router.events,
    setIsShowingMaavaron,
    closeMaavaron,
    maavronConfig.mobile,
    maavronConfig.desktop,
    navDirectionRef,
  ])

  // TODO: Remove full screen size setting
  // const [size, setSize] = useState<googletag.SingleSizeArray | null>(null)

  //for ROS maavaron on mobile
  useEffect(() => {
    const messageEvent = (e: MessageEvent) => {
      if (e.data === 'ROS_MAAVRON_CLOSED') {
        setOutOfPage(true)
        setIsShowingMaavaron(false)
        clearInterstitialTimeOut()
        taboolaLog({ toBeLogged: 'interstitial closed' })
      }
    }
    window.addEventListener('message', messageEvent)

    return () => {
      window.removeEventListener('message', messageEvent)
    }
  }, [maavronTimeout, router.events, setIsShowingMaavaron])

  // useEffect(() => {
  //   utils.initPageViewedInSession()
  // }, [])

  // useEffect(() => {
  //   const pv = utils.getPageViewedInSession() + 1
  //   utils.setPageViewedInSession(pv)
  //   pageViewedInSession.current = pv
  //   // Math.max(pv, pageViewedInSession.current + 1)
  // }, [router.asPath])

  useEffect(() => {
    if (!MaavaronId) {
      return
    }

    if (navDirection === 'back') {
      // The underlying ad slot remounts (and re-requests) on every asPath
      // change while it's showing, since its key includes asPath (see
      // AdWithKey in sharedComponents/Ads/Ad.tsx). Silently skipping this
      // block left `showMaavaron` at whatever it was before — if it was
      // already true, the ad kept remounting/firing on every subsequent
      // navigation, back or forward, regardless of direction. Explicitly
      // turning it off here is what actually stops that.
      setIsShowingMaavaron(false)
      setShowMaavaron(false)
      return
    }

    let shuldShow = false
    switch (MaavaronId) {
      case 'Maavaron_Desktop':
        shuldShow = sessionPageView >= 2 && sessionPageView % 2 == 0
        break
      case activeRos:
        shuldShow = sessionPageView > 1
        // if (shuldShow) {
        //   shuldShow = getDailyClicksCount() % 3 == 0
        // }
        break
    }

    if (shuldShow) {
      setOutOfPage(!shuldShow)
    } else {
      setIsShowingMaavaron(false)
    }

    taboolaLog({
      toBeLogged: {
        msg: `interstitial request ${shuldShow ? '' : 'not '}send to dfp`,
      },
    })
    // prettyLog('Maavaron: 225', { shuldShow, sessionPageView })
    setShowMaavaron(shuldShow)
  }, [
    MaavaronId,
    setIsShowingMaavaron,
    dailyClicksCount,
    sessionPageView,
    navDirection,
  ])

  const onRender = useCallback(
    (event: googletag.events.SlotRenderEndedEvent) => {
      // If the ad unit fills, bring ad popup into view and increment the ad display counter
      ottLog({ m: 'Maavaron', isEmpty: event.isEmpty })
      if (!event.isEmpty) {
        setOutOfPage(false)
        incrementMaavaronCounter()
        // setIsShowingMaavaron(true)
      } else {
        setOutOfPage(true)
        setIsShowingMaavaron(false)
      }
      setSlotRenderEventReceived({
        received: true,
        isEmpty: event.isEmpty,
      })
    },
    [setIsShowingMaavaron]
  )

  const onSkip = () => {
    taboolaLog({ toBeLogged: { msg: 'interstitial closed' } })
    clearInterstitialTimeOut()
    setOutOfPage(true)
    setIsShowingMaavaron(false)
  }

  useEffect(() => {
    if (slotRenderEventReceived.received) {
      if (slotRenderEventReceived.isEmpty) {
        clearInterstitialTimeOut()
      }
      taboolaLog({
        toBeLogged: {
          msg: `event SlotRenderEndedEvent isEmpty ${
            slotRenderEventReceived.isEmpty ? 'true' : 'false'
          } received`,
        },
      })
    }
  }, [slotRenderEventReceived.isEmpty, slotRenderEventReceived.received])

  // Deriving this synchronously during render (rather than only reacting to
  // `navDirection` in the effect above) matters: `direction` flips to
  // 'back' synchronously inside router.beforePopState(), but the effect
  // that turns `showMaavaron` off is inherently deferred until after this
  // render commits. In that gap, Next's own (async) asPath update can still
  // land and remount the ad slot (keyed by asPath — see AdWithKey in
  // sharedComponents/Ads/Ad.tsx) while `showMaavaron` is still stale-true.
  // Gating the render output directly on `navDirection` closes that gap —
  // there's no effect-flush delay left to race against.
  const isVisible = navDirection !== 'back' && showMaavaron

  if (!MaavaronId || !isVisible) {
    return null
  }

  return (
    <Container
      id={`Container-${MaavaronId}`}
      outOfPage={outOfPage}
      theme={{
        pageType: theme,
        id: MaavaronId,
        show:
          slotRenderEventReceived.isEmpty === false &&
          slotRenderEventReceived.received,
      }}
      isFlDarkTheme={isDarkTheme}
      key={'interstitial'}
    >
      {MaavaronId === 'Maavaron_Desktop' ? (
        <Content>
          <Strip theme={{ type: theme }}>
            {theme !== 'ott' && !isFaceLift && <VodLogo />}
            {theme !== 'ott' && !isFaceLift && <PortalLogo />}
            <SkipButton
              onClick={onSkip}
              children={`${he.skip}`}
              fontColor={fontColor}
              theme={{ type: theme, isFaceLift: isFaceLift }}
            />
          </Strip>
          <Ad id={MaavaronId} collapse={true} onRender={onRender} />
        </Content>
      ) : (
        <Ad id={MaavaronId as AdId} collapse={true} onRender={onRender} />
      )}
    </Container>
  )
}

const maavaronCounterKey = 'maavaronCounter'
const incrementMaavaronCounter = () => {
  setMaavaronCounter(getMaavaronCounter() + 1)
}

const getMaavaronCounter = () => {
  const counterString = sessionStorage.getItem(maavaronCounterKey)
  const counter = counterString && parseInt(counterString, 10)

  if (counter) {
    return counter
  }

  setMaavaronCounter(0)
  return 0
}

const setMaavaronCounter = (value: number) => {
  sessionStorage.setItem(maavaronCounterKey, JSON.stringify(value))
}
